import type {
  RadarRailItinerary,
  RadarRailResponse,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/rail';
import type {
  RailItinerary,
  RailItineraryProps,
  RailLeg,
  RailLegProps,
  RailPrice,
} from '@web-platform/shared-universal-types/src/redux/state/entities/rail';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

export const mapRailResults = (
  response: Maybe<RadarRailResponse>,
): {
  byBucket: Record<string, RailItinerary[]>;
  partnerLink: string;
} => {
  if (!response || !response.buckets) {
    return {
      byBucket: {},
      partnerLink: '',
    };
  }
  const results: Record<string, RailItinerary[]> = {};
  let partnerLink = '';
  response.buckets.forEach((bucket) => {
    if (bucket) {
      const { id: bucketId, items: bucketItems } = bucket;
      results[bucketId] = bucketItems.map(
        ({ deeplink, eco, id, legs, price }: RadarRailItinerary) => ({
          id,
          deeplink,
          price,
          eco,
          legs,
        }),
      );

      if (bucketItems && bucketItems.length > 0) {
        partnerLink = bucketItems[0].redirectUrl;
      }
    }
  });

  return {
    byBucket: results,
    partnerLink,
  };
};

export const mapItinerary = (
  itinerary: RailItinerary,
  index: number,
  passengers: number,
): RailItineraryProps => {
  const duplicateCarriers = itinerary.legs.flatMap((leg) =>
    leg.segments.map((segment) => ({
      code: segment.transport.carrier.code,
      logoUrl: segment.transport.carrier.logoUrl,
      name: segment.transport.carrier.name,
    })),
  );

  const carriers = Array.from(
    new Map(duplicateCarriers.map((carrier) => [carrier.code, carrier])).values(),
  ).slice(0, 2);

  const isReturn = itinerary.legs.length > 1;

  const outBoundLeg = mapLeg(itinerary.legs[0], true, isReturn);

  const legs = [outBoundLeg];
  if (isReturn) {
    const inBoundLeg = mapLeg(itinerary.legs[1], false, isReturn);
    legs.push(inBoundLeg);
  }
  const price = mapPrice(itinerary, passengers);

  return {
    id: itinerary.id,
    deeplink: itinerary.deeplink,
    carriers,
    legs,
    price,
    optionIndex: index,
  };
};

export const mapLeg = (leg: RailLeg, isOutbound: boolean, isReturn: boolean): RailLegProps => ({
  origin: leg.origin.name,
  destination: leg.destination.name,
  departureDateTime: leg.departure,
  arrivalDateTime: leg.arrival,
  duration: leg.durationInMinutes,
  stopCount: leg.changeCount,
  isOutbound,
  isReturn,
  operator: leg.segments[0].transport.carrier.name,
});

export const mapPrice = (itinerary: RailItinerary, passengers: number): RailPrice => ({
  perPassenger: itinerary.price.raw / passengers,
  totalAmount: itinerary.price.raw,
  passengers,
});
